import { ReactNode, useMemo } from 'react';
import {
  AnimalCow,
  FieldCrop,
  Landscape,
  Money,
  Wrench,
  Home,
  ChartBar,
  Settings,
  Package,
  Refresh,
  FileText,
  Briefcase,
} from '../components/icons/IconLibrary';
import type { Action, Module } from '../types/permissions';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Grupo de navegación para organizar el sidebar en secciones plegables.
 * Reemplaza la lista plana de 10 ítems por agrupación lógica.
 */
export type NavGroup = 'principal' | 'ganado' | 'campo' | 'finanzas' | 'sistema';

/** Orden de render de los grupos en el sidebar. */
export const NAV_GROUP_ORDER: NavGroup[] = ['principal', 'ganado', 'campo', 'finanzas', 'sistema'];

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  principal: 'Principal',
  ganado: 'Ganado',
  campo: 'Campo',
  finanzas: 'Finanzas',
  sistema: 'Sistema',
};

export interface MenuItem {
  path: string;
  label: string;
  icon: ReactNode;
  /** Grupo para la sección del sidebar. Default: 'principal'. */
  group?: NavGroup;
  /**
   * Módulo requerido para ver/usar el item. Si no se setea, el item es visible
   * para todo usuario autenticado (ej. dashboard, página principal).
   */
  module?: Module;
  /** Acción requerida sobre el módulo. Default: 'view'. */
  action?: Action;
  /**
   * Ancla data-ar-guide explícita (default: `menu-${pathToAnchor(path)}` en
   * Sidebar/Header). Permite conservar anclas históricas cuando un item se
   * muda de lugar — ej. Mapa/Lluvias, que eran tiles de Herramientas.
   */
  anchor?: string;
}

// Iconos SVG reutilizables - ahora usando IconLibrary
export const Icons = {
  home: <Home size={22} />,
  dashboard: <ChartBar size={22} />,
  animals: <AnimalCow size={22} />,
  locations: <FieldCrop size={22} />,
  fields: <Landscape size={22} />,
  admin: <Money size={22} />,
  tools: <Wrench size={22} />,
  reports: <ChartBar size={22} />,
  add: <Refresh size={20} />,
  addSmall: <Refresh size={18} />,
  report: <FileText size={20} />,
  chart: <ChartBar size={20} />,
  purchaseSale: <Refresh size={20} />,
  building: <Briefcase size={22} />,
  sessions: <Package size={22} />,
  sigsa: <FileText size={22} />,
  settings: <Settings size={22} />,
  opinion: (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5z" />
    </svg>
  ),
};

// Items del menú principal de navegación, agrupados por sección.
export const menuItems: MenuItem[] = [
  // Principal
  { path: '/', label: 'Inicio', icon: Icons.home, group: 'principal' },
  { path: '/dashboard', label: 'Panel principal', icon: Icons.dashboard, group: 'principal' },
  // Mapa del Campo y Lluvias: páginas propias en Principal (/mapa y /lluvias).
  {
    path: '/mapa',
    label: 'Mapa',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    ),
    group: 'principal',
    module: 'tools',
  },
  {
    path: '/lluvias',
    label: 'Lluvias',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
        <line x1="8" y1="19" x2="8" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <line x1="16" y1="19" x2="16" y2="21" />
      </svg>
    ),
    group: 'principal',
    module: 'tools',
  },
  // Ganado
  { path: '/animals', label: 'Mi Ganado', icon: Icons.animals, group: 'ganado', module: 'animals' },
  {
    // Mover animales en masa: navega a Mi Ganado con ?mover=1, que abre el
    // modal de mover (spec 2026-09-30-mover-animales-masivo).
    path: '/animals?mover=1',
    label: 'Mover Animales',
    // Ancla fija (la generada por pathToAnchor llevaría el "?mover=1"): la usan las
    // guías AR del asistente ("¿cómo muevo animales?").
    anchor: 'menu-mover-animales',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
    group: 'ganado',
    module: 'animals_history',
    action: 'create',
  },
  { path: '/sesiones', label: 'Sesiones de Carga', icon: Icons.sessions, group: 'ganado', module: 'animals' },
  { path: '/declaracion-sigsa', label: 'Declaración SIGSA', icon: Icons.sigsa, group: 'ganado', module: 'sigsa' },
  // Reportes: sección propia dentro de Ganado (antes era acción rápida — se
  // conserva el ancla histórica qa-reports para guías/tutoriales/e2e).
  { path: '/reports', label: 'Reporte del Campo', icon: Icons.report, group: 'ganado', module: 'reports', anchor: 'qa-reports' },
  { path: '/analisis-descendencia', label: 'Análisis Descendencia', icon: Icons.chart, group: 'ganado', module: 'animals' },
  // Campo (página unificada: campos + ubicaciones)
  { path: '/ubicaciones', label: 'Campos y Ubicaciones', icon: Icons.locations, group: 'campo', module: 'locations' },
  { path: '/campo/stock-analysis', label: 'Movimientos de Hacienda', icon: Icons.chart, group: 'campo', module: 'locations' },
  // Finanzas
  { path: '/administracion', label: 'Administración', icon: Icons.admin, group: 'finanzas', module: 'financial' },
  {
    path: '/operaciones-compra-venta',
    label: 'Compra y Venta',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="17 1 21 5 17 9" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <polyline points="7 23 3 19 7 15" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
    group: 'finanzas',
    module: 'financial',
  },
  // Sistema
  { path: '/herramientas', label: 'Herramientas', icon: Icons.tools, group: 'sistema', module: 'tools' },
  { path: '/configuraciones', label: 'Configuraciones', icon: Icons.settings, group: 'sistema', module: 'admin' },
  { path: '/admin/usuarios', label: 'Usuarios', icon: Icons.building, group: 'sistema', module: 'admin' },
  // Sin `module`: cualquier usuario (todos los roles) puede dejar su opinión.
  { path: '/mi-opinion', label: 'Dejá tu opinión', icon: Icons.opinion, group: 'sistema' },
];

// Acciones rápidas (se muestran en menú desktop y mobile).
// Mismo orden que las "Acciones Rápidas" del panel principal
// (DashboardPage.tsx): Carga Rápida primero, después Nueva Sesión.
export const quickActions: MenuItem[] = [
  {
    path: '/animals?carga-rapida=1',
    label: 'Carga Rápida',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    module: 'animals_history',
    action: 'create',
  },
  { path: '/animals/new', label: 'Nueva Sesión', icon: Icons.addSmall, module: 'animals', action: 'create' },
  // Acciones financieras más frecuentes para el administrador: abren los
  // modales únicos (⚡rápida o 🎯con caravanas) via deep-link.
  { path: '/operaciones-compra-venta?nueva-venta=1', label: 'Nueva Venta', icon: Icons.addSmall, module: 'financial', action: 'create' },
  { path: '/operaciones-compra-venta?nueva-compra=1', label: 'Nueva Compra', icon: Icons.addSmall, module: 'financial', action: 'create' },
];

/**
 * Filtra una lista de items por permiso del usuario en la empresa activa.
 * Los items sin `module` son visibles para todo autenticado.
 */
const filterByPermission = (
  items: MenuItem[],
  can: (m: Module, a: Action) => boolean,
): MenuItem[] => items.filter((item) => !item.module || can(item.module, item.action ?? 'view'));

/**
 * Hook que devuelve los items del menú y acciones rápidas filtrados por los
 * permisos del usuario en la empresa activa.
 */
export const useMenuItems = () => {
  const { can } = usePermissions();
  const filteredMenu = useMemo(() => filterByPermission(menuItems, can), [can]);
  const filteredQuickActions = useMemo(() => filterByPermission(quickActions, can), [can]);
  return { menuItems: filteredMenu, quickActions: filteredQuickActions };
};
